import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import {
  getOrderHistory,
  getBrokerageAccount,
  type Order,
  type BrokerageAccount,
} from "../services/brokerageService";

// Group orders by date
interface OrderGroup {
  date: string;
  dateLabel: string;
  orders: Order[];
}

export function BrokerageOrderHistoryPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const [account, setAccount] = useState<BrokerageAccount | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (!accountId) {
      navigate("/brokerage");
      return;
    }
    loadData();
  }, [accountId, days, navigate]);

  async function loadData() {
    if (!accountId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const [accountData, ordersData] = await Promise.all([
        getBrokerageAccount(accountId),
        getOrderHistory(accountId, days),
      ]);
      
      setAccount(accountData);
      setOrders(ordersData || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load order history");
    } finally {
      setLoading(false);
    }
  }

  function formatCurrency(value: string | undefined): string {
    if (!value) return "-";
    const num = parseFloat(value);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(num);
  }

  function formatQty(value: string | undefined): string {
    if (!value) return "-";
    const num = parseFloat(value);
    if (Number.isInteger(num)) return num.toString();
    return num.toFixed(4);
  }

  function formatDateTime(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function getStatusBadge(status: string): { bg: string; text: string } {
    switch (status.toLowerCase()) {
      case "filled":
        return { bg: "bg-accent-500/20", text: "text-accent-400" };
      case "partially_filled":
        return { bg: "bg-yellow-500/20", text: "text-yellow-400" };
      case "canceled":
      case "cancelled":
        return { bg: "bg-surface-600", text: "text-surface-300" };
      case "expired":
        return { bg: "bg-orange-500/20", text: "text-orange-400" };
      case "rejected":
      case "failed":
        return { bg: "bg-red-500/20", text: "text-red-400" };
      case "new":
      case "accepted":
      case "pending_new":
        return { bg: "bg-blue-500/20", text: "text-blue-400" };
      default:
        return { bg: "bg-surface-600", text: "text-surface-300" };
    }
  }

  function getSideBadge(side: string): { bg: string; text: string } {
    return side.toLowerCase() === "buy"
      ? { bg: "bg-accent-500/20", text: "text-accent-400" }
      : { bg: "bg-red-500/20", text: "text-red-400" };
  }

  // Group orders by date
  function groupOrdersByDate(orders: Order[]): OrderGroup[] {
    const groups: Map<string, Order[]> = new Map();
    
    for (const order of orders) {
      const date = new Date(order.submitted_at);
      // Use local date string to avoid timezone issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateKey = `${year}-${month}-${day}`; // YYYY-MM-DD in local time
      
      if (!groups.has(dateKey)) {
        groups.set(dateKey, []);
      }
      groups.get(dateKey)!.push(order);
    }
    
    // Sort by date descending and convert to array
    const sortedKeys = Array.from(groups.keys()).sort((a, b) => b.localeCompare(a));
    
    return sortedKeys.map(dateKey => {
      // Parse dateKey back to a date for formatting (use noon to avoid timezone issues)
      const [y, m, d] = dateKey.split('-').map(Number);
      const localDate = new Date(y, m - 1, d, 12, 0, 0);
      return {
        date: dateKey,
        dateLabel: localDate.toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        orders: groups.get(dateKey)!,
      };
    });
  }

  const orderGroups = groupOrdersByDate(orders);

  return (
    <div>
      <PageHeader
        title={account ? `Order History - ${account.account_name}` : "Order History"}
        subtitle="View your recent trading activity from Alpaca"
        backgroundImage="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1920&q=80"
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        {/* Breadcrumb & Controls */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <Link 
            to="/brokerage" 
            className="text-primary-400 hover:text-primary-300 transition-colors inline-flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Brokerage Accounts
          </Link>

          <div className="flex items-center gap-2">
            <label className="text-sm text-surface-400">Show last:</label>
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg text-surface-100 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="mb-6 glass-card bg-red-500/10 border-red-500/30 text-red-400 px-4 py-3">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="spinner w-8 h-8" />
              <p className="text-surface-400">Loading order history...</p>
            </div>
          </div>
        ) : orders.length === 0 ? (
          <div className="glass-card p-6 sm:p-12 text-center">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-surface-800/50 flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-surface-100 mb-2">No Orders</h3>
            <p className="text-surface-400">
              No orders found in the last {days} days.
            </p>
          </div>
        ) : (
          <>
            {/* Summary */}
            <div className="glass-card p-4 mb-6">
              <p className="text-surface-400">
                <span className="text-surface-100 font-medium">{orders.length}</span> orders in the last{" "}
                <span className="text-surface-100 font-medium">{days}</span> days
              </p>
            </div>

            {/* Orders grouped by day */}
            <div className="space-y-6">
              {orderGroups.map((group) => (
                <div key={group.date}>
                  {/* Day separator */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="h-px flex-1 bg-surface-700" />
                    <h3 className="text-sm font-medium text-surface-300 whitespace-nowrap">
                      {group.dateLabel}
                    </h3>
                    <div className="h-px flex-1 bg-surface-700" />
                  </div>

                  {/* Desktop Table */}
                  <div className="glass-card overflow-hidden hidden md:block">
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr className="border-b border-surface-700">
                            <th className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">Time</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">Symbol</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">Side</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-surface-400 uppercase tracking-wider">Type</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-surface-400 uppercase tracking-wider">Qty</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-surface-400 uppercase tracking-wider">Filled</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-surface-400 uppercase tracking-wider">Avg Price</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-surface-400 uppercase tracking-wider">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-surface-700/50">
                          {group.orders.map((order) => {
                            const statusBadge = getStatusBadge(order.status);
                            const sideBadge = getSideBadge(order.side);
                            
                            return (
                              <tr key={order.id} className="hover:bg-surface-800/50 transition-colors">
                                <td className="px-4 py-3 text-surface-300 text-sm">
                                  {formatDateTime(order.submitted_at)}
                                </td>
                                <td className="px-4 py-3 font-medium text-surface-100">{order.symbol}</td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-1 text-xs font-medium rounded ${sideBadge.bg} ${sideBadge.text}`}>
                                    {order.side.toUpperCase()}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-surface-300 capitalize">{order.type}</td>
                                <td className="px-4 py-3 text-right text-surface-300">
                                  {order.qty ? formatQty(order.qty) : order.notional ? formatCurrency(order.notional) : "-"}
                                </td>
                                <td className="px-4 py-3 text-right text-surface-300">{formatQty(order.filled_qty)}</td>
                                <td className="px-4 py-3 text-right text-surface-300">{formatCurrency(order.filled_avg_price)}</td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`px-2 py-1 text-xs font-medium rounded ${statusBadge.bg} ${statusBadge.text}`}>
                                    {order.status.replace(/_/g, " ")}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile Cards */}
                  <div className="md:hidden space-y-3">
                    {group.orders.map((order) => {
                      const statusBadge = getStatusBadge(order.status);
                      const sideBadge = getSideBadge(order.side);
                      
                      return (
                        <div key={order.id} className="glass-card p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-semibold text-surface-100">{order.symbol}</span>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded ${sideBadge.bg} ${sideBadge.text}`}>
                                {order.side.toUpperCase()}
                              </span>
                            </div>
                            <span className={`px-2 py-1 text-xs font-medium rounded ${statusBadge.bg} ${statusBadge.text}`}>
                              {order.status.replace(/_/g, " ")}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-surface-400">Time</p>
                              <p className="text-surface-200">{formatDateTime(order.submitted_at)}</p>
                            </div>
                            <div>
                              <p className="text-surface-400">Type</p>
                              <p className="text-surface-200 capitalize">{order.type}</p>
                            </div>
                            <div>
                              <p className="text-surface-400">Qty</p>
                              <p className="text-surface-200">
                                {order.qty ? formatQty(order.qty) : order.notional ? formatCurrency(order.notional) : "-"}
                              </p>
                            </div>
                            <div>
                              <p className="text-surface-400">Filled</p>
                              <p className="text-surface-200">{formatQty(order.filled_qty)}</p>
                            </div>
                            {order.filled_avg_price && (
                              <div className="col-span-2">
                                <p className="text-surface-400">Avg Fill Price</p>
                                <p className="text-surface-200">{formatCurrency(order.filled_avg_price)}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
